import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { getUserId } from "../authutils";
import { API_ROUTES } from "../../routes/apiRoutes";

export const useClientProjects = (searchQuery, status) => {
  return useInfiniteQuery({
    queryKey: ["client-projects", getUserId(), searchQuery, status],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get(API_ROUTES.PROJECT_DETAILS, {
        params: {
          search_query: searchQuery || undefined,
          client_project_status: status || undefined,
          page: pageParam,
          per_page: 10,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.pagination?.has_next ? allPages.length + 1 : undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
};

/** Paginated project list filtered by the raw `status` enum value (e.g. "Delivered"). */
export const useProjectsByStatus = (status, { searchQuery = "", enabled = true } = {}) => {
  return useInfiniteQuery({
    queryKey: ["projects-by-status", getUserId(), status, searchQuery],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get(API_ROUTES.PROJECT_DETAILS, {
        params: {
          status,
          search_query: searchQuery || undefined,
          page: pageParam,
          per_page: 10,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.pagination?.has_next ? allPages.length + 1 : undefined,
    enabled: enabled && !!status,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
};

export const usePDFExport = ({ page = 1, pageSize = 10, search = "", from = "", to = "" } = {}) => {
  return useQuery({
    queryKey: ["client-projects-pdf", getUserId(), page, pageSize, search, from, to],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.PROJECT_DETAILS_PDF, {
        params: {
          search_query: search || undefined,
          from_date: from || undefined,
          to_date: to || undefined,
          page,
          perPage: pageSize,
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });
};

export const useClientProjectById = (projectId) => {
  return useQuery({
    queryKey: ["client-project", getUserId(), projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const response = await api.get(API_ROUTES.PROJECT_DETAIL(projectId));
      return response.data.data;
    },
  });
};
